const initialized = new WeakMap();

export function initializeAmbienceGallery(gallery) {
  if (initialized.has(gallery)) return initialized.get(gallery);

  const slides = [...gallery.querySelectorAll('[data-ambience-slide]')];
  const thumbnails = [...gallery.querySelectorAll('[data-ambience-thumb]')];
  const fallback = gallery.querySelector('[data-ambience-fallback]');
  const controls = gallery.querySelector('[data-ambience-controls]');
  const previous = gallery.querySelector('[data-ambience-prev]');
  const next = gallery.querySelector('[data-ambience-next]');
  const counter = gallery.querySelector('[data-ambience-counter]');
  const counterTemplate = counter?.getAttribute('data-ambience-counter-template') || '__current__ / __total__';
  const items = slides.slice(0, 7).map((slide, index) => ({
    slide,
    image: slide.querySelector('img'),
    thumbnail: thumbnails.find(thumbnail => Number(thumbnail.getAttribute('data-ambience-thumb')) === index),
    failed: false,
    index,
  }));
  let selected = items[0] || null;
  let gesture = null;

  const available = () => items.filter(item => !item.failed);
  const render = () => {
    const valid = available();
    if (!valid.includes(selected)) selected = valid.find(item => item.index > (selected?.index ?? -1)) || valid[0] || null;
    const selectedIndex = valid.indexOf(selected);
    gallery.setAttribute('data-ambience-state', valid.length > 1 ? 'multiple' : valid.length === 1 ? 'single' : 'empty');
    gallery.setAttribute('data-ambience-count', String(valid.length));
    for (const slide of slides) {
      const active = selected?.slide === slide;
      slide.hidden = !active;
      slide.setAttribute('aria-hidden', String(!active));
    }
    for (const thumbnail of thumbnails) {
      const item = items.find(entry => entry.thumbnail === thumbnail);
      const active = Boolean(item && item === selected);
      thumbnail.hidden = !item || item.failed;
      thumbnail.disabled = !item || item.failed;
      thumbnail.setAttribute('aria-pressed', String(active));
      thumbnail.tabIndex = active ? 0 : -1;
    }
    if (fallback) {
      fallback.hidden = valid.length !== 0;
      fallback.setAttribute('aria-hidden', String(valid.length !== 0));
    }
    if (controls) controls.hidden = valid.length < 2;
    if (previous) previous.disabled = valid.length < 2;
    if (next) next.disabled = valid.length < 2;
    if (counter) counter.textContent = valid.length ? counterTemplate.replaceAll('__current__', String(selectedIndex + 1)).replaceAll('__total__', String(valid.length)) : '';
  };
  const show = index => {
    const valid = available();
    if (!valid.length) return;
    const hadThumbnailFocus = items.some(item => item.thumbnail === gallery.ownerDocument?.activeElement);
    selected = valid[((index % valid.length) + valid.length) % valid.length];
    if (selected.image) selected.image.loading = 'eager';
    render();
    if (hadThumbnailFocus) selected.thumbnail?.focus();
  };
  const move = direction => {
    const valid = available();
    if (valid.length > 1) show(valid.indexOf(selected) + direction);
  };
  const fail = item => {
    if (item.failed) return;
    const hadFocus = item.thumbnail === gallery.ownerDocument?.activeElement;
    const hadControlFocus = controls?.contains(gallery.ownerDocument?.activeElement);
    item.failed = true;
    render();
    if (hadFocus) {
      if (available().length > 1 && selected?.thumbnail) selected.thumbnail.focus();
      else gallery.focus();
    } else if (hadControlFocus && available().length < 2) gallery.focus();
  };

  previous?.addEventListener('click', () => move(-1));
  next?.addEventListener('click', () => move(1));
  for (const item of items) {
    item.thumbnail?.addEventListener('click', () => {
      if (!item.failed) show(available().indexOf(item));
    });
    if (!item.image) {
      item.failed = true;
      continue;
    }
    item.image.addEventListener('error', () => fail(item));
    if (item.image.complete && item.image.naturalWidth === 0) item.failed = true;
    const thumbnailImage = item.thumbnail?.querySelector('img');
    const showThumbnailNumber = () => {
      thumbnailImage.hidden = true;
      const number = item.thumbnail.querySelector('[data-ambience-thumb-fallback]');
      if (number) number.hidden = false;
      else item.thumbnail.textContent = String(item.index + 1);
    };
    if (thumbnailImage) {
      thumbnailImage.addEventListener('error', showThumbnailNumber);
      if (thumbnailImage.complete && thumbnailImage.naturalWidth === 0) showThumbnailNumber();
    }
  }

  gallery.addEventListener('keydown', event => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.target?.isContentEditable || ['INPUT', 'SELECT', 'TEXTAREA'].includes(event.target?.tagName)) return;
    if (available().length < 2) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      show(event.key === 'Home' ? 0 : available().length - 1);
    }
  });
  gallery.addEventListener('touchstart', event => {
    gesture = null;
    if (event.touches.length !== 1 || event.target?.closest('button, a, input, select, textarea')) return;
    const touch = event.touches[0];
    gesture = { x: touch.clientX, y: touch.clientY, id: touch.identifier };
  }, { passive: true });
  gallery.addEventListener('touchend', event => {
    const origin = gesture;
    gesture = null;
    if (!origin || event.touches.length) return;
    const touch = [...event.changedTouches].find(point => point.identifier === origin.id);
    if (!touch) return;
    const horizontal = touch.clientX - origin.x;
    const vertical = touch.clientY - origin.y;
    if (Math.abs(horizontal) >= 40 && Math.abs(horizontal) > Math.abs(vertical) * 1.5) move(horizontal < 0 ? 1 : -1);
  }, { passive: true });
  gallery.addEventListener('touchcancel', () => { gesture = null; }, { passive: true });

  const controller = { refresh: render };
  initialized.set(gallery, controller);
  render();
  return controller;
}

export function initializeAmbienceGalleries(container) {
  if (container.matches?.('[data-ambience-gallery]')) initializeAmbienceGallery(container);
  for (const gallery of container.querySelectorAll('[data-ambience-gallery]')) initializeAmbienceGallery(gallery);
}

if (typeof document !== 'undefined') {
  initializeAmbienceGalleries(document);
  document.addEventListener('shopify:section:load', event => initializeAmbienceGalleries(event.target));
}
